import { Outlet, NavLink, Link, useParams, useNavigate, useLocation } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useAuthStore } from '@/stores/authStore'
import { useSearchStore } from '@/stores/searchStore'
import { authApi } from '@/api/auth'
import { getInboxSummary } from '@/api/requests'
import { cn } from '@/lib/utils'
import NotificationBell from '@/components/NotificationBell'
import {
  LayoutDashboard,
  Clock,
  LogOut,
  Menu,
  X,
  Users,
  Settings,
  Calendar,
  CalendarDays,
  FileText,
  ClipboardCheck,
  Package,
  Building2,
  Wallet,
  UserPlus,
  ChevronDown,
  User,
  Search,
  BarChart3,
  Shield,
  ClipboardList,
  UserRound,
  Inbox,
  CheckSquare,
} from 'lucide-react'
import { useState, useEffect } from 'react'
import { useMyCaps } from '@/lib/capabilities'

export default function Layout() {
  const { orgSlug } = useParams<{ orgSlug: string }>()
  const { user, logout } = useAuthStore()
  const location = useLocation()
  const { query, setQuery } = useSearchStore()

  // Reset the header search whenever the route changes.
  useEffect(() => { setQuery('') }, [location.pathname, setQuery])
  const navigate = useNavigate()
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [userMenuOpen, setUserMenuOpen] = useState(false)

  const base = `/${orgSlug}`
  const { has: can } = useMyCaps()
  const canAdmin = can('admin_settings')
  const canViewHR = can('view_analytics')
  const isAdmin = user?.role === 'admin'  // still used for the profile-menu badge

  // Feature modules enabled for this org (super-admin controlled). While loading
  // we default to "all enabled" so nav never flickers empty.
  const { data: features } = useQuery({
    queryKey: ['features', orgSlug],
    queryFn: authApi.getFeatures,
    staleTime: 60_000,
    enabled: !!user,
  })
  const has = (key?: string) => !key || !features || features.includes(key)
  // Approvals inbox: shown to anyone who approves something (managers of a team,
  // attendance / payroll / HR / leave approvers), with a pending count.
  const { data: inbox } = useQuery({
    queryKey: ['inbox-summary', user?.id],
    queryFn: getInboxSummary,
    enabled: !!user,
    refetchInterval: 120_000,
  })
  // Same rule as App.tsx IndexRoute: these users' "Dashboard" is the HR dashboard.
  const landsOnHrDashboard = can('admin_settings') || can('view_analytics')

  /* ─── Nav items matching reference images (each gated by an optional feature) ─── */
  const mainNav = [
    // For admin/HR, the index route (`base`) redirects to /admin/dashboard —
    // matchPaths keeps this single item highlighted on both URLs so it never
    // looks like a separate "HR Dashboard" destination.
    { name: 'Dashboard', href: base, icon: LayoutDashboard, end: true, matchPaths: [`${base}/admin/dashboard`] },
    // Admins/HR land on the HR dashboard; this gives them their own personal view.
    ...(landsOnHrDashboard ? [{ name: 'My Dashboard', href: `${base}/me`, icon: UserRound }] : []),
    { name: 'My Attendance', href: `${base}/attendance`, icon: ClipboardCheck, feature: 'attendance' },
    { name: 'Leaves', href: `${base}/leaves`, icon: Calendar, feature: 'leaves' },
    { name: 'Requests', href: `${base}/requests`, icon: Inbox },
    ...(inbox?.can_review ? [{ name: 'Approvals', href: `${base}/approvals`, icon: CheckSquare, badge: inbox.pending }] : []),
    { name: 'Payslips', href: `${base}/payslips`, icon: Wallet, feature: 'payroll' },
    { name: 'Timesheet', href: `${base}/timesheet`, icon: Clock, feature: 'timesheets' },
  ].filter((i) => has((i as any).feature))

  // Staff management — each item gated by its capability (and org feature).
  const staffNav = [
    { name: 'Attendance', href: `${base}/admin/attendance`, icon: ClipboardList, feature: 'attendance', cap: 'manage_attendance' },
    { name: 'Users', href: `${base}/admin/users`, icon: Users, cap: 'manage_users' },
    { name: 'Organization', href: `${base}/admin/organization`, icon: Building2, cap: 'manage_hr' },
    { name: 'Onboarding', href: `${base}/admin/onboarding`, icon: UserPlus, feature: 'onboarding', cap: 'manage_hr' },
    { name: 'Payroll', href: `${base}/admin/payroll`, icon: Wallet, feature: 'payroll', cap: 'manage_payroll' },
    { name: 'Inventory', href: `${base}/admin/inventory`, icon: Package, feature: 'inventory', cap: 'manage_inventory' },
    // Leave approvals queue — admins and anyone whose role has Manage Leave.
    { name: 'Manage Leaves', href: `${base}/admin/manage-leaves`, icon: CalendarDays, feature: 'leaves', cap: 'manage_leave' },
  ].filter((i) => has((i as any).feature) && can((i as any).cap))
  const canManageInventory = staffNav.length > 0

  // HR performance & reports portal
  const hrNav = [
    { name: 'HR Analytics', href: `${base}/admin/analytics`, icon: BarChart3, feature: 'reports' },
  ].filter((i) => has((i as any).feature) && canViewHR)

  // Admin-only management
  const adminNav = [
    { name: 'Roles & Permissions', href: `${base}/admin/roles`, icon: Shield },
    { name: 'Invoices', href: `${base}/admin/invoices`, icon: FileText, feature: 'invoices' },
  ].filter((i) => has((i as any).feature))

  const handleLogout = () => {
    setUserMenuOpen(false)
    logout()
    navigate(`${base}/login`)
  }

  const renderNavLink = (item: { name: string; href: string; icon: any; end?: boolean; matchPaths?: string[]; badge?: number }) => {
    // Some destinations (e.g. the HR dashboard) are reachable at a second URL
    // besides their own `href` — treat those as active too, so a single nav
    // item never looks unselected just because a redirect landed elsewhere.
    const extraActive = item.matchPaths?.includes(location.pathname) ?? false
    return (
      <NavLink
        key={item.name}
        to={item.href}
        end={'end' in item ? item.end : undefined}
        onClick={() => setSidebarOpen(false)}
        className={({ isActive }) =>
          cn(
            'flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors',
            (isActive || extraActive)
              ? 'bg-[#EC5B2C] text-white'
              : 'text-gray-400 hover:bg-white/10 hover:text-white'
          )
        }
      >
        <item.icon className="h-[18px] w-[18px]" />
        <span className="flex-1">{item.name}</span>
        {!!item.badge && (
          <span className="rounded-full bg-amber-400 px-1.5 py-0.5 text-[10px] font-semibold leading-none text-gray-900">
            {item.badge > 99 ? '99+' : item.badge}
          </span>
        )}
      </NavLink>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar — dark charcoal */}
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-50 w-64 bg-[#1E2A3A] transform transition-transform lg:translate-x-0 flex flex-col',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        {/* Sidebar header */}
        <div className="flex h-14 items-center justify-between px-5 shrink-0">
          <img src="/logo-light.png" alt="Managition" className="h-7" />
          <button
            className="lg:hidden text-gray-400 hover:text-white"
            onClick={() => setSidebarOpen(false)}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Nav items — scrollable */}
        <nav className="flex-1 overflow-y-auto no-scrollbar px-3 py-2 space-y-1">
          {mainNav.map(renderNavLink)}

          {canManageInventory && (
            <>
              <div className="pt-4 pb-1 px-3 text-[10px] font-semibold text-gray-500 uppercase tracking-widest">
                Management
              </div>
              {staffNav.map(renderNavLink)}
            </>
          )}

          {canViewHR && hrNav.length > 0 && (
            <>
              <div className="pt-4 pb-1 px-3 text-[10px] font-semibold text-gray-500 uppercase tracking-widest">
                HR &amp; Reports
              </div>
              {hrNav.map(renderNavLink)}
            </>
          )}

          {canAdmin && (
            <>
              <div className="pt-4 pb-1 px-3 text-[10px] font-semibold text-gray-500 uppercase tracking-widest">
                Admin
              </div>
              {adminNav.map(renderNavLink)}
            </>
          )}
        </nav>
      </aside>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Top bar — orange-yellow gradient */}
        <header className="sticky top-0 z-30 h-14 bg-gradient-to-r from-[#EC5B2C] to-[#F7A94B] flex items-center px-4 lg:px-6 shadow-sm">
          <button
            className="lg:hidden mr-3 text-white/90 hover:text-white"
            onClick={() => setSidebarOpen(true)}
          >
            <Menu className="h-5 w-5" />
          </button>

          <img src="/logo-light.png" alt="Managition" className="h-6 lg:hidden mr-auto" />

          <div className="flex-1" />

          {/* Search + notifications + user menu */}
          <div className="flex items-center gap-2 sm:gap-2.5">
            <div className="relative hidden sm:block">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-white/70" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search here…"
                aria-label="Search"
                className="w-40 md:w-56 rounded-full bg-white/15 pl-8 pr-3 py-1.5 text-sm text-white placeholder-white/70 outline-none ring-1 ring-white/25 focus:bg-white/25 focus:ring-white/50 transition"
              />
            </div>
            <NotificationBell />

            <div className="relative">
              <button
                onClick={() => setUserMenuOpen((o) => !o)}
                className="flex items-center gap-2 rounded-full py-1 pl-2.5 pr-1.5 hover:bg-white/10 transition-colors"
                aria-haspopup="menu"
                aria-expanded={userMenuOpen}
              >
                <span className="hidden sm:block text-sm font-medium text-white max-w-[140px] truncate">{user?.name}</span>
                <div className="h-8 w-8 rounded-full bg-white/20 flex items-center justify-center shrink-0 ring-2 ring-white/30">
                  {user?.avatar_url ? (
                    <img src={user.avatar_url} alt={user.name} className="h-8 w-8 rounded-full" />
                  ) : (
                    <span className="text-xs font-semibold text-white">{user?.name?.charAt(0).toUpperCase()}</span>
                  )}
                </div>
                <ChevronDown className={cn('h-4 w-4 text-white/80 transition-transform', userMenuOpen && 'rotate-180')} />
              </button>

              {userMenuOpen && (
                <>
                  {/* click-away backdrop */}
                  <div className="fixed inset-0 z-40" onClick={() => setUserMenuOpen(false)} />
                  <div
                    role="menu"
                    className="absolute right-0 mt-2 w-60 rounded-xl bg-white shadow-lg ring-1 ring-black/5 z-50 overflow-hidden"
                  >
                    <div className="px-4 py-3 border-b border-gray-100">
                      <p className="text-sm font-semibold text-gray-900 truncate">{user?.name}</p>
                      <p className="text-xs text-gray-500 truncate">{user?.email}</p>
                      {(isAdmin || user?.role === 'accounts' || user?.role === 'hr') && (
                        <span className="mt-1.5 inline-block rounded-full bg-orange-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-[#EC5B2C]">
                          {isAdmin ? 'Admin' : user?.role === 'hr' ? 'HR' : 'Accounts'}
                        </span>
                      )}
                    </div>

                    <div className="py-1">
                      <Link
                        to={`${base}/settings`}
                        onClick={() => setUserMenuOpen(false)}
                        className="flex items-center gap-2.5 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                        role="menuitem"
                      >
                        <User className="h-4 w-4 text-gray-400" />
                        Profile &amp; Settings
                      </Link>
                      {isAdmin && (
                        <Link
                          to={`${base}/admin/settings`}
                          onClick={() => setUserMenuOpen(false)}
                          className="flex items-center gap-2.5 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                          role="menuitem"
                        >
                          <Settings className="h-4 w-4 text-gray-400" />
                          Admin Settings
                        </Link>
                      )}
                    </div>

                    <div className="border-t border-gray-100 py-1">
                      <button
                        onClick={handleLogout}
                        className="flex w-full items-center gap-2.5 px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                        role="menuitem"
                      >
                        <LogOut className="h-4 w-4" />
                        Sign out
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="p-4 lg:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  )
}
